import { StandardSummary } from "../standards/standards";
import { checkMetadataOf, FrontDoorCheckMetadata } from "./checkMetadata";

/** The catalog annotation naming a Component's GitHub repo, `owner/repo`. @public */
export const GITHUB_SLUG_ANNOTATION = "github.com/project-slug";

/** `owner/repo` from a project-slug annotation value. @public */
export function parseGithubSlug(
  slug: unknown
): { owner: string; repo: string } | undefined {
  const m =
    typeof slug === "string" ? slug.trim().match(/^([\w.-]+)\/([\w.-]+)$/) : null;
  return m ? { owner: m[1], repo: m[2] } : undefined;
}

/** @public */
export interface FixLink {
  title: string;
  url: string;
}

/** A standard a check cites. @public */
export interface CheckStandard {
  id: string;
  /** Absent when the registry has no entry for the id, or can't be read. */
  registry?: Pick<StandardSummary, "title" | "status" | "url">;
}

/** The cited standard, with the registry's title, status and page when it has the id. @public */
export function standardOf(
  standardId: string | undefined,
  index?: ReadonlyMap<string, StandardSummary>
): CheckStandard | undefined {
  if (!standardId) return undefined;
  const found = index?.get(standardId);
  return found
    ? {
        id: standardId,
        registry: { title: found.title, status: found.status, url: found.url },
      }
    : { id: standardId };
}

/** A CheckStandard as the flat fields the MCP actions return. @public */
export function standardFields(standard: CheckStandard | undefined) {
  return {
    standardId: standard?.id,
    standardTitle: standard?.registry?.title,
    standardStatus: standard?.registry?.status,
    standardUrl: standard?.registry?.url,
  };
}

/** What fix links read from a check. @public */
export interface FixableCheck {
  links?: ReadonlyArray<{ title: string; url: string }>;
  metadata?: Record<string, unknown> | FrontDoorCheckMetadata;
}

const metadataOf = (check: FixableCheck) =>
  checkMetadataOf(check.metadata as Record<string, unknown> | undefined);

/** A short title for a fix URL: its link's title, a known docs site, or the host. @public */
export function fixTitle(url: string, check: FixableCheck): string {
  if (url.startsWith("/")) {
    return check.links?.find((l) => l.url === url)?.title ?? "Open";
  }
  const { standardId } = metadataOf(check);
  if (url.includes("blackboard-standards") && standardId) {
    return `${standardId} standard`;
  }
  if (url.includes("docs.github.com")) return "GitHub docs";
  if (url.includes("backstage.io")) return "Backstage docs";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "Open";
  }
}

/**
 * Where to fix a check: the page in the service's repo (`githubFixPath`, when
 * the service has a GitHub slug), else `fixUrl`, else the first link, else the
 * cited standard's page.
 * @public
 */
export function checkFixLink(
  check: FixableCheck,
  options: { githubSlug?: string; standard?: CheckStandard }
): FixLink | undefined {
  const metadata = metadataOf(check);
  const repo = parseGithubSlug(options.githubSlug);
  if (metadata.githubFixPath && repo) {
    return {
      title: metadata.githubFixPath.title,
      url: `https://github.com/${repo.owner}/${repo.repo}${metadata.githubFixPath.path}`,
    };
  }
  if (metadata.fixUrl) {
    return { title: fixTitle(metadata.fixUrl, check), url: metadata.fixUrl };
  }
  const link = check.links?.[0];
  if (link) return { title: link.title, url: link.url };
  const registry = options.standard?.registry;
  return registry
    ? { title: `${options.standard!.id} standard`, url: registry.url }
    : undefined;
}
